import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useSIEMAlerts,
  useIncidents,
  useThreatHunts,
  useDarkWebMonitors,
  useSOCDashboard,
  useEscalationMetrics,
  updateAlert,
  createAlert,
  createIncident,
} from '../../hooks/useSecOps';
import { useSignalRRefresh } from '../../hooks/useSignalR';

// ─── Constants ─────────────────────────────────────────────────

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-blue-100 text-blue-700',
  info: 'bg-gray-100 text-gray-600',
};

const STATUS_COLORS: Record<string, string> = {
  new: 'bg-blue-100 text-blue-700',
  open: 'bg-red-100 text-red-700',
  investigating: 'bg-yellow-100 text-yellow-700',
  in_progress: 'bg-yellow-100 text-yellow-700',
  contained: 'bg-orange-100 text-orange-700',
  acknowledged: 'bg-indigo-100 text-indigo-700',
  eradicated: 'bg-teal-100 text-teal-700',
  resolved: 'bg-green-100 text-green-700',
  recovered: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 text-gray-600',
  false_positive: 'bg-gray-100 text-gray-500',
};

const SOURCE_ICONS: Record<string, { icon: string; color: string }> = {
  EDR: { icon: 'M9 3v18h6V3H9z', color: 'text-red-500' },
  endpoint: { icon: 'M9 3v18h6V3H9z', color: 'text-red-500' },
  SIEM: { icon: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z', color: 'text-indigo-500' },
  cloud: { icon: 'M19.35 10.04A7.49 7.49 0 0012 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 000 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z', color: 'text-blue-500' },
  M365: { icon: 'M19.35 10.04A7.49 7.49 0 0012 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 000 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z', color: 'text-blue-500' },
  network: { icon: 'M21 6H3a1 1 0 00-1 1v4a1 1 0 001 1h18a1 1 0 001-1V7a1 1 0 00-1-1zM21 14H3a1 1 0 00-1 1v4a1 1 0 001 1h18a1 1 0 001-1v-4a1 1 0 00-1-1z', color: 'text-green-500' },
  firewall: { icon: 'M21 6H3a1 1 0 00-1 1v4a1 1 0 001 1h18a1 1 0 001-1V7a1 1 0 00-1-1zM21 14H3a1 1 0 00-1 1v4a1 1 0 001 1h18a1 1 0 001-1v-4a1 1 0 00-1-1z', color: 'text-green-500' },
  threat_intel: { icon: 'M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z', color: 'text-purple-500' },
  manual: { icon: 'M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z', color: 'text-gray-500' },
};

// Full MITRE ATT&CK Tactics
const MITRE_TACTICS = [
  { id: 'TA0043', name: 'Reconnaissance', short: 'Recon' },
  { id: 'TA0042', name: 'Resource Development', short: 'Res Dev' },
  { id: 'TA0001', name: 'Initial Access', short: 'Init Access' },
  { id: 'TA0002', name: 'Execution', short: 'Execution' },
  { id: 'TA0003', name: 'Persistence', short: 'Persistence' },
  { id: 'TA0004', name: 'Privilege Escalation', short: 'Priv Esc' },
  { id: 'TA0005', name: 'Defense Evasion', short: 'Def Evasion' },
  { id: 'TA0006', name: 'Credential Access', short: 'Cred Access' },
  { id: 'TA0007', name: 'Discovery', short: 'Discovery' },
  { id: 'TA0008', name: 'Lateral Movement', short: 'Lat Move' },
  { id: 'TA0009', name: 'Collection', short: 'Collection' },
  { id: 'TA0011', name: 'Command and Control', short: 'C2' },
  { id: 'TA0010', name: 'Exfiltration', short: 'Exfil' },
  { id: 'TA0040', name: 'Impact', short: 'Impact' },
];

// Technique-to-tactic mapping (common techniques)
const TECHNIQUE_TACTIC_MAP: Record<string, string> = {
  'T1595': 'TA0043', 'T1592': 'TA0043', 'T1589': 'TA0043', 'T1590': 'TA0043',
  'T1583': 'TA0042', 'T1584': 'TA0042', 'T1587': 'TA0042', 'T1588': 'TA0042',
  'T1566': 'TA0001', 'T1190': 'TA0001', 'T1133': 'TA0001', 'T1078': 'TA0001', 'T1199': 'TA0001', 'T1189': 'TA0001',
  'T1059': 'TA0002', 'T1047': 'TA0002', 'T1053': 'TA0002', 'T1203': 'TA0002', 'T1106': 'TA0002', 'T1204': 'TA0002',
  'T1547': 'TA0003', 'T1053.005': 'TA0003', 'T1136': 'TA0003', 'T1543': 'TA0003', 'T1546': 'TA0003',
  'T1548': 'TA0004', 'T1134': 'TA0004', 'T1068': 'TA0004', 'T1055': 'TA0004',
  'T1140': 'TA0005', 'T1070': 'TA0005', 'T1036': 'TA0005', 'T1027': 'TA0005', 'T1562': 'TA0005', 'T1218': 'TA0005', 'T1112': 'TA0005',
  'T1110': 'TA0006', 'T1003': 'TA0006', 'T1552': 'TA0006', 'T1558': 'TA0006', 'T1556': 'TA0006',
  'T1087': 'TA0007', 'T1082': 'TA0007', 'T1083': 'TA0007', 'T1057': 'TA0007', 'T1018': 'TA0007', 'T1033': 'TA0007', 'T1049': 'TA0007', 'T1016': 'TA0007',
  'T1021': 'TA0008', 'T1080': 'TA0008', 'T1570': 'TA0008', 'T1210': 'TA0008',
  'T1560': 'TA0009', 'T1005': 'TA0009', 'T1039': 'TA0009', 'T1074': 'TA0009', 'T1113': 'TA0009',
  'T1071': 'TA0011', 'T1095': 'TA0011', 'T1573': 'TA0011', 'T1090': 'TA0011', 'T1105': 'TA0011', 'T1132': 'TA0011', 'T1219': 'TA0011',
  'T1041': 'TA0010', 'T1048': 'TA0010', 'T1567': 'TA0010', 'T1029': 'TA0010', 'T1030': 'TA0010',
  'T1486': 'TA0040', 'T1489': 'TA0040', 'T1490': 'TA0040', 'T1529': 'TA0040', 'T1485': 'TA0040',
};

// ─── Shared Components ─────────────────────────────────────────

function Badge({ value, colors }: { value: string; colors: Record<string, string> }) {
  const label = value.replace(/_/g, ' ');
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${colors[value] ?? 'bg-gray-100 text-gray-600'}`}>
      {label}
    </span>
  );
}

function StatCard({ label, value, color, subtitle, onClick }: { label: string; value: string | number; color: string; subtitle?: string; onClick?: () => void }) {
  return (
    <button onClick={onClick} className={`bg-white rounded-lg shadow p-5 text-left w-full hover:ring-2 hover:ring-indigo-200 transition ${onClick ? 'cursor-pointer' : ''}`}>
      <p className="text-3xl font-bold text-gray-900">{typeof value === 'number' ? value.toLocaleString() : value}</p>
      <p className="text-sm text-gray-500 mt-1">{label}</p>
      {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
      <div className={`mt-3 h-1 w-12 rounded ${color}`} />
    </button>
  );
}

function SourceIcon({ source }: { source: string }) {
  const info = SOURCE_ICONS[source] ?? SOURCE_ICONS['manual'];
  return (
    <svg className={`w-4 h-4 ${info.color} flex-shrink-0`} viewBox="0 0 24 24" fill="currentColor">
      <path d={info.icon} />
    </svg>
  );
}

function MitreBadge({ technique }: { technique: string }) {
  return (
    <span className="inline-flex items-center rounded bg-purple-50 px-1.5 py-0.5 text-[10px] font-mono text-purple-700 border border-purple-200">
      {technique}
    </span>
  );
}

function formatMinutes(minutes: number | null): string {
  if (minutes == null) return 'N/A';
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 1440) return `${Math.round(minutes / 60)}h`;
  return `${Math.round(minutes / 1440)}d`;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ─── Sub-Components ────────────────────────────────────────────

function ExecutiveSummary({ dashboard, incidents }: {
  dashboard: { executive: { total_alerts_30d: number; critical_alerts: number; high_alerts: number; open_incidents: number; resolved_incidents_30d: number; avg_mttd_minutes: number | null; avg_mttr_minutes: number | null; security_score: number | null }; score_trend: Array<{ score: number; date: string }>; top_categories: Array<{ category: string; count: number }> } | null;
  incidents: Record<string, unknown>[];
}) {
  const exec = dashboard?.executive;
  const score = exec?.security_score;
  const scoreGrade = score == null ? 'N/A' : score >= 90 ? 'A' : score >= 80 ? 'B' : score >= 70 ? 'C' : score >= 60 ? 'D' : 'F';
  const scoreColor = score == null ? 'text-gray-400' : score >= 80 ? 'text-green-600' : score >= 60 ? 'text-yellow-600' : 'text-red-600';
  const openIncidents = exec?.open_incidents ?? incidents.filter(i => i.status !== 'closed' && i.status !== 'resolved').length;
  const incidentColor = openIncidents === 0 ? 'bg-green-500' : openIncidents <= 2 ? 'bg-yellow-500' : 'bg-red-500';

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-base font-semibold text-gray-900 mb-4">Executive Summary</h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
        <div className="text-center">
          <p className={`text-3xl font-bold ${scoreColor}`}>{scoreGrade}</p>
          <p className="text-xs text-gray-500 mt-1">Security Score</p>
          {score != null && <p className="text-xs text-gray-400">{score}/100</p>}
        </div>
        <div className="text-center">
          <p className="text-3xl font-bold text-gray-900">{exec?.total_alerts_30d ?? 0}</p>
          <p className="text-xs text-gray-500 mt-1">Alerts (30d)</p>
        </div>
        <div className="text-center">
          <p className="text-3xl font-bold text-red-600">{exec?.critical_alerts ?? 0}</p>
          <p className="text-xs text-gray-500 mt-1">Critical</p>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-1.5">
            <span className={`w-2.5 h-2.5 rounded-full ${incidentColor}`} />
            <p className="text-3xl font-bold text-gray-900">{openIncidents}</p>
          </div>
          <p className="text-xs text-gray-500 mt-1">Open Incidents</p>
        </div>
        <div className="text-center">
          <p className="text-3xl font-bold text-indigo-600">{formatMinutes(exec?.avg_mttd_minutes ?? null)}</p>
          <p className="text-xs text-gray-500 mt-1">MTTD</p>
        </div>
        <div className="text-center">
          <p className="text-3xl font-bold text-indigo-600">{formatMinutes(exec?.avg_mttr_minutes ?? null)}</p>
          <p className="text-xs text-gray-500 mt-1">MTTR</p>
        </div>
        <div className="text-center">
          <p className="text-3xl font-bold text-green-600">{exec?.resolved_incidents_30d ?? 0}</p>
          <p className="text-xs text-gray-500 mt-1">Resolved (30d)</p>
        </div>
      </div>

      {/* Score Trend Mini-Chart */}
      {dashboard?.score_trend && dashboard.score_trend.length > 1 && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-500 mb-2">Security Score Trend (30d)</p>
          <div className="flex items-end gap-0.5 h-12">
            {dashboard.score_trend.map((s, i) => (
              <div
                key={i}
                className={`flex-1 rounded-t ${s.score >= 80 ? 'bg-green-400' : s.score >= 60 ? 'bg-yellow-400' : 'bg-red-400'}`}
                style={{ height: `${Math.max(4, (s.score / 100) * 48)}px` }}
                title={`${s.date}: ${s.score}`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Top Threat Categories */}
      {dashboard?.top_categories && dashboard.top_categories.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-500 mb-2">Top Threat Categories This Month</p>
          <div className="flex flex-wrap gap-2">
            {dashboard.top_categories.map(c => (
              <span key={c.category} className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-700">
                <span className="font-medium capitalize">{c.category}</span>
                <span className="text-gray-400">({c.count})</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function UnifiedAlertFeed({ alerts, navigate, onAcknowledge, onEscalate, onDismiss }: {
  alerts: Record<string, unknown>[];
  navigate: (path: string) => void;
  onAcknowledge: (id: string) => void;
  onEscalate: (id: string) => void;
  onDismiss: (id: string) => void;
}) {
  const recentAlerts = [...alerts]
    .sort((a, b) => new Date((b.detected_at as string) ?? (b.created_at as string) ?? '').getTime() - new Date((a.detected_at as string) ?? (a.created_at as string) ?? '').getTime())
    .slice(0, 15);

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Unified Alert Feed</h2>
          <p className="text-xs text-gray-400">Cross-product alerts from all sources</p>
        </div>
        <button onClick={() => navigate('/secops/alerts')} className="text-sm text-indigo-600 hover:text-indigo-800 font-medium">View all</button>
      </div>
      {recentAlerts.length === 0 ? (
        <div className="px-6 py-10 text-center text-sm text-gray-400">No alerts. All systems clear.</div>
      ) : (
        <div className="divide-y divide-gray-50 max-h-[500px] overflow-y-auto">
          {recentAlerts.map((alert, i) => {
            const mitre = (alert.mitre_techniques as string[]) ?? [];
            const source = (alert.source as string) ?? 'manual';
            const isNew = alert.status === 'new';
            return (
              <div key={i} className={`px-5 py-3 hover:bg-gray-50 transition ${isNew ? 'bg-blue-50/30' : ''}`}>
                <div className="flex items-start gap-3">
                  <SourceIcon source={source} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button onClick={() => navigate(`/secops/alerts/${alert.id}`)} className="text-sm font-medium text-gray-900 hover:text-indigo-600 truncate max-w-xs text-left">
                        {alert.title as string}
                      </button>
                      <Badge value={alert.severity as string} colors={SEVERITY_COLORS} />
                      <Badge value={alert.status as string} colors={STATUS_COLORS} />
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      <span>{source}</span>
                      {alert.company_id ? <span>Tenant: {String(alert.company_id)}</span> : null}
                      {(alert.source_endpoint_id || alert.assigned_to) ? (
                        <span>{String(alert.source_endpoint_id || alert.assigned_to)}</span>
                      ) : null}
                      <span>{alert.detected_at ? timeAgo(alert.detected_at as string) : ''}</span>
                    </div>
                    {mitre.length > 0 && (
                      <div className="flex gap-1 mt-1.5 flex-wrap">
                        {mitre.slice(0, 4).map(t => <MitreBadge key={t} technique={t} />)}
                        {mitre.length > 4 && <span className="text-[10px] text-gray-400">+{mitre.length - 4}</span>}
                      </div>
                    )}
                  </div>
                  {isNew && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={(e) => { e.stopPropagation(); onAcknowledge(alert.id as string); }} title="Acknowledge"
                        className="p-1.5 rounded hover:bg-indigo-100 text-indigo-600 transition">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); onEscalate(alert.id as string); }} title="Escalate"
                        className="p-1.5 rounded hover:bg-red-100 text-red-600 transition">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" /></svg>
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); onDismiss(alert.id as string); }} title="Dismiss"
                        className="p-1.5 rounded hover:bg-gray-200 text-gray-400 transition">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function IncidentTimeline({ incidents, navigate }: { incidents: Record<string, unknown>[]; navigate: (path: string) => void }) {
  const activeIncidents = incidents
    .filter(i => i.status !== 'closed' && i.status !== 'false_positive')
    .sort((a, b) => new Date((b.opened_at as string) ?? '').getTime() - new Date((a.opened_at as string) ?? '').getTime())
    .slice(0, 8);

  const statusStages = ['open', 'investigating', 'contained', 'eradicated', 'recovered', 'closed'];

  function getStageIndex(status: string): number {
    const idx = statusStages.indexOf(status);
    return idx >= 0 ? idx : 0;
  }

  const severityDotColor: Record<string, string> = {
    critical: 'bg-red-500',
    high: 'bg-orange-500',
    medium: 'bg-yellow-500',
    low: 'bg-blue-400',
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Incident Timeline</h2>
          <p className="text-xs text-gray-400">Active incident lifecycle progress</p>
        </div>
        <button onClick={() => navigate('/secops/incidents')} className="text-sm text-indigo-600 hover:text-indigo-800 font-medium">View all</button>
      </div>
      {activeIncidents.length === 0 ? (
        <div className="px-6 py-10 text-center text-sm text-gray-400">No active incidents. All clear.</div>
      ) : (
        <div className="divide-y divide-gray-50 max-h-[400px] overflow-y-auto">
          {activeIncidents.map((inc, i) => {
            const stageIdx = getStageIndex(inc.status as string);
            const severity = (inc.severity as string) ?? 'medium';
            const progress = Math.round(((stageIdx + 1) / statusStages.length) * 100);

            return (
              <div key={i} className="px-5 py-3.5 hover:bg-gray-50 cursor-pointer transition" onClick={() => navigate(`/secops/incidents/${inc.id}`)}>
                <div className="flex items-center gap-3 mb-2">
                  <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${severityDotColor[severity] ?? 'bg-gray-400'}`} />
                  <p className="text-sm font-medium text-gray-900 truncate flex-1">{inc.title as string}</p>
                  <Badge value={severity} colors={SEVERITY_COLORS} />
                </div>
                {/* Progress bar */}
                <div className="ml-5">
                  <div className="flex items-center gap-1 mb-1">
                    {statusStages.map((stage, si) => (
                      <div key={stage} className="flex items-center flex-1">
                        <div className={`h-1.5 rounded-full flex-1 ${si <= stageIdx ? (severity === 'critical' ? 'bg-red-400' : severity === 'high' ? 'bg-orange-400' : 'bg-indigo-400') : 'bg-gray-200'}`} />
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between text-[10px] text-gray-400">
                    <span className="capitalize">{(inc.status as string).replace(/_/g, ' ')}</span>
                    <span>{progress}%</span>
                    <span>{inc.opened_at ? timeAgo(inc.opened_at as string) : ''}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MitreHeatMap({ mitreHits, alerts, navigate }: {
  mitreHits: Record<string, number>;
  alerts: Record<string, unknown>[];
  navigate: (path: string) => void;
}) {
  const [selectedTactic, setSelectedTactic] = useState<string | null>(null);

  // Group techniques by tactic
  const tacticTechniques: Record<string, { id: string; count: number }[]> = {};
  for (const tactic of MITRE_TACTICS) {
    tacticTechniques[tactic.id] = [];
  }

  for (const [tech, count] of Object.entries(mitreHits)) {
    const baseId = tech.split('.')[0];
    const tacticId = TECHNIQUE_TACTIC_MAP[baseId] ?? TECHNIQUE_TACTIC_MAP[tech];
    if (tacticId && tacticTechniques[tacticId]) {
      tacticTechniques[tacticId].push({ id: tech, count });
    }
  }

  // Coverage stats
  const tacticsWithDetections = MITRE_TACTICS.filter(t => tacticTechniques[t.id].length > 0).length;
  const coveragePct = Math.round((tacticsWithDetections / MITRE_TACTICS.length) * 100);
  const totalDetections = Object.values(mitreHits).reduce((a, b) => a + b, 0);

  // Max count for heat scaling
  const maxCount = Math.max(1, ...Object.values(mitreHits));

  function heatColor(count: number): string {
    if (count === 0) return 'bg-gray-100 text-gray-400';
    const intensity = count / maxCount;
    if (intensity > 0.7) return 'bg-red-500 text-white';
    if (intensity > 0.4) return 'bg-orange-400 text-white';
    if (intensity > 0.2) return 'bg-yellow-400 text-gray-800';
    return 'bg-yellow-200 text-gray-700';
  }

  // Get alerts for selected tactic
  const selectedAlerts = selectedTactic
    ? alerts.filter(a => {
        const techs = (a.mitre_techniques as string[]) ?? [];
        const tacticTechs = tacticTechniques[selectedTactic]?.map(t => t.id) ?? [];
        return techs.some(t => tacticTechs.includes(t));
      }).slice(0, 5)
    : [];

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-900">MITRE ATT&CK Heat Map</h2>
            <p className="text-xs text-gray-400">Detection coverage across the ATT&CK framework (30 days)</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-2xl font-bold text-indigo-600">{coveragePct}%</span>
            <span className="text-xs text-gray-500">tactic coverage</span>
          </div>
        </div>
        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
          <span>{totalDetections} total detections</span>
          <span>{tacticsWithDetections}/{MITRE_TACTICS.length} tactics triggered</span>
          <div className="flex items-center gap-1">
            <span className="w-3 h-2 bg-gray-100 rounded" /> None
            <span className="w-3 h-2 bg-yellow-200 rounded" /> Low
            <span className="w-3 h-2 bg-orange-400 rounded" /> Med
            <span className="w-3 h-2 bg-red-500 rounded" /> High
          </div>
        </div>
      </div>

      <div className="px-4 py-3 overflow-x-auto">
        <div className="flex gap-1" style={{ minWidth: '900px' }}>
          {MITRE_TACTICS.map(tactic => {
            const techs = tacticTechniques[tactic.id];
            const totalCount = techs.reduce((a, b) => a + b.count, 0);
            const isSelected = selectedTactic === tactic.id;

            return (
              <button
                key={tactic.id}
                onClick={() => setSelectedTactic(isSelected ? null : tactic.id)}
                className={`flex-1 min-w-[60px] rounded-lg p-2 text-center transition border-2 ${isSelected ? 'border-indigo-500 ring-1 ring-indigo-200' : 'border-transparent hover:border-gray-300'}`}
              >
                <div className={`rounded-md p-2 mb-1.5 ${totalCount > 0 ? heatColor(totalCount) : 'bg-gray-100'}`}>
                  <p className="text-lg font-bold">{totalCount}</p>
                </div>
                <p className="text-[10px] font-medium text-gray-700 leading-tight">{tactic.short}</p>
                {techs.length > 0 && (
                  <p className="text-[9px] text-gray-400 mt-0.5">{techs.length} tech{techs.length !== 1 ? 's' : ''}</p>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Drill-down panel */}
      {selectedTactic && (
        <div className="border-t border-gray-200 px-5 py-3 bg-gray-50">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-700">
              {MITRE_TACTICS.find(t => t.id === selectedTactic)?.name} — Techniques
            </p>
            <button onClick={() => setSelectedTactic(null)} className="text-xs text-gray-400 hover:text-gray-600">Close</button>
          </div>
          {tacticTechniques[selectedTactic].length === 0 ? (
            <p className="text-xs text-gray-400">No detections for this tactic.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {tacticTechniques[selectedTactic].map(t => (
                <span key={t.id} className={`rounded px-2 py-1 text-xs font-mono ${heatColor(t.count)}`}>
                  {t.id} ({t.count})
                </span>
              ))}
            </div>
          )}
          {selectedAlerts.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs text-gray-500 font-medium">Recent Alerts:</p>
              {selectedAlerts.map((a, i) => (
                <button key={i} onClick={() => navigate(`/secops/alerts/${a.id}`)}
                  className="block w-full text-left text-xs px-2 py-1.5 rounded hover:bg-white transition">
                  <span className="font-medium text-gray-800">{a.title as string}</span>
                  <span className="text-gray-400 ml-2">{a.detected_at ? timeAgo(a.detected_at as string) : ''}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CrossProductHealthStrip() {
  // Integration health data — shows connection status for cross-product integrations
  // In production, this would call integration/health-ext or check real-time status
  const integrations = [
    { name: 'Defend (EDR)', key: 'defend', statusText: 'Endpoint telemetry', icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
    { name: 'RMM', key: 'rmm', statusText: 'Device management', icon: 'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
    { name: 'Backups', key: 'backups', statusText: 'Backup status', icon: 'M4 7v10c0 2.21 3.58 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.58 4 8 4s8-1.79 8-4M4 7c0-2.21 3.58-4 8-4s8 1.79 8 4' },
    { name: 'M365', key: 'm365', statusText: 'Cloud monitoring', icon: 'M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z' },
    { name: 'On-Call', key: 'oncall', statusText: 'Schedule status', icon: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z' },
  ];

  // Simulated health — in production, these come from the backend
  const healthStatus: Record<string, { status: 'green' | 'yellow' | 'red' | 'gray'; detail: string }> = {
    defend: { status: 'green', detail: 'Telemetry active' },
    rmm: { status: 'green', detail: 'Connected' },
    backups: { status: 'yellow', detail: 'Check status' },
    m365: { status: 'green', detail: 'Monitoring active' },
    oncall: { status: 'gray', detail: 'Not configured' },
  };

  const statusDot: Record<string, string> = {
    green: 'bg-green-400',
    yellow: 'bg-yellow-400',
    red: 'bg-red-500',
    gray: 'bg-gray-300',
  };

  return (
    <div className="bg-white rounded-lg shadow px-5 py-4">
      <h2 className="text-sm font-semibold text-gray-900 mb-3">Integration Health</h2>
      <div className="flex gap-3 overflow-x-auto">
        {integrations.map(int => {
          const health = healthStatus[int.key];
          return (
            <div key={int.key} className="flex items-center gap-2.5 bg-gray-50 rounded-lg px-3 py-2.5 min-w-[160px]">
              <svg className="w-5 h-5 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={int.icon} />
              </svg>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full ${statusDot[health.status]}`} />
                  <p className="text-xs font-medium text-gray-800">{int.name}</p>
                </div>
                <p className="text-[10px] text-gray-400 truncate">{health.detail}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Browser Notifications ─────────────────────────────────────

function useAlertNotifications(alerts: Record<string, unknown>[], enabled: boolean) {
  const prevAlertIds = useRef<Set<string>>(new Set());
  const initialized = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    const currentIds = new Set(alerts.map(a => a.id as string));

    // Skip first render to avoid spamming on page load
    if (!initialized.current) {
      prevAlertIds.current = currentIds;
      initialized.current = true;
      return;
    }

    // Find new alerts that are P1 or P2
    const newAlerts = alerts.filter(a =>
      !prevAlertIds.current.has(a.id as string) &&
      (a.severity === 'critical' || a.severity === 'high') &&
      a.status === 'new'
    );

    for (const alert of newAlerts) {
      if (Notification.permission === 'granted') {
        new Notification(`[${(alert.severity as string).toUpperCase()}] ${alert.title as string}`, {
          body: `Source: ${alert.source as string} | ${alert.detected_at ? timeAgo(alert.detected_at as string) : ''}`,
          icon: '/favicon.ico',
          tag: `alert-${alert.id}`,
        });
      }
    }

    prevAlertIds.current = currentIds;
  }, [alerts, enabled]);
}

function useTabBadge(count: number) {
  useEffect(() => {
    const baseTitle = 'SOC Command Center';
    document.title = count > 0 ? `(${count}) ${baseTitle}` : baseTitle;
    return () => { document.title = baseTitle; };
  }, [count]);
}

// ─── Main Page ─────────────────────────────────────────────────

export default function SecurityCommandCenterPage() {
  const navigate = useNavigate();
  const [showCreateAlert, setShowCreateAlert] = useState(false);
  const [showCreateIncident, setShowCreateIncident] = useState(false);
  const [newAlert, setNewAlert] = useState({ title: '', severity: 'medium', category: '', source: '' });
  const [newIncident, setNewIncident] = useState({ title: '', severity: 'medium', description: '' });
  const [creating, setCreating] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);

  // Data hooks — auto-refreshing + SignalR push
  const { data: alertsData, loading: alertsLoading, refresh: refreshAlerts } = useSIEMAlerts();
  const { data: incidentsData, loading: incidentsLoading, refresh: refreshIncidents } = useIncidents();
  const { data: huntsData, loading: huntsLoading } = useThreatHunts();
  const { data: darkWebData, loading: darkWebLoading } = useDarkWebMonitors();
  const { data: dashboardData, loading: dashboardLoading, refresh: refreshDashboard } = useSOCDashboard();
  const { data: escalationData } = useEscalationMetrics();

  // Real-time updates via SignalR (fall back to polling if unavailable)
  useSignalRRefresh('siemAlert', refreshAlerts);
  useSignalRRefresh('incidentUpdate', refreshIncidents);
  useSignalRRefresh('dashboardUpdate', refreshDashboard);

  const alerts = (alertsData?.alerts ?? []) as Record<string, unknown>[];
  const incidents = (incidentsData?.incidents ?? []) as Record<string, unknown>[];
  const hunts = (huntsData?.hunts ?? []) as Record<string, unknown>[];
  const darkWebFindings = (darkWebData?.findings ?? []) as Record<string, unknown>[];
  const dashboard = dashboardData?.data ?? null;

  // Counts
  const criticalAlerts = alerts.filter(a => a.severity === 'critical').length;
  const openIncidents = incidents.filter(i => i.status !== 'closed' && i.status !== 'resolved').length;
  const activeHunts = hunts.filter(h => h.status === 'in_progress' || h.status === 'active').length;
  const exposures = darkWebFindings.filter(f => (f as Record<string, unknown>).status !== 'resolved').length;
  const newAlertCount = alerts.filter(a => a.status === 'new' && (a.severity === 'critical' || a.severity === 'high')).length;

  // MITRE data from dashboard or derived from alerts
  const mitreHits = dashboard?.mitre_hits ?? (() => {
    const counts: Record<string, number> = {};
    for (const a of alerts) {
      const techs = (a.mitre_techniques as string[]) ?? [];
      for (const t of techs) counts[t] = (counts[t] ?? 0) + 1;
    }
    return counts;
  })();

  // Browser notifications
  useAlertNotifications(alerts, notificationsEnabled);
  useTabBadge(newAlertCount);

  // Request notification permission
  const requestNotificationPermission = useCallback(async () => {
    if (!('Notification' in window)) return;
    if (Notification.permission === 'granted') {
      setNotificationsEnabled(true);
      return;
    }
    if (Notification.permission !== 'denied') {
      const perm = await Notification.requestPermission();
      setNotificationsEnabled(perm === 'granted');
    }
  }, []);

  // Quick actions
  const handleAcknowledge = useCallback(async (id: string) => {
    try { await updateAlert(id, { status: 'acknowledged' }); refreshAlerts(); } catch { /* ignore */ }
  }, [refreshAlerts]);

  const handleEscalate = useCallback(async (id: string) => {
    try { await updateAlert(id, { status: 'escalated' }); refreshAlerts(); } catch { /* ignore */ }
  }, [refreshAlerts]);

  const handleDismiss = useCallback(async (id: string) => {
    try { await updateAlert(id, { status: 'dismissed' }); refreshAlerts(); } catch { /* ignore */ }
  }, [refreshAlerts]);

  async function handleCreateAlert() {
    if (!newAlert.title) return;
    setCreating(true);
    try {
      await createAlert(newAlert);
      setShowCreateAlert(false);
      setNewAlert({ title: '', severity: 'medium', category: '', source: '' });
      refreshAlerts();
    } catch { /* handled by UI */ } finally {
      setCreating(false);
    }
  }

  async function handleCreateIncident() {
    if (!newIncident.title) return;
    setCreating(true);
    try {
      await createIncident(newIncident);
      setShowCreateIncident(false);
      setNewIncident({ title: '', severity: 'medium', description: '' });
    } catch { /* handled by UI */ } finally {
      setCreating(false);
    }
  }

  const loading = alertsLoading || incidentsLoading || huntsLoading || darkWebLoading || dashboardLoading;

  if (loading) {
    return (
      <div className="px-4 md:px-8 py-6 max-w-[1400px] mx-auto">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-64" />
          <div className="h-32 bg-gray-200 rounded-lg" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => <div key={i} className="h-28 bg-gray-200 rounded-lg" />)}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-96 bg-gray-200 rounded-lg" />
            <div className="h-96 bg-gray-200 rounded-lg" />
          </div>
          <div className="h-48 bg-gray-200 rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-8 py-6 max-w-[1400px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Security Command Center</h1>
          <p className="text-sm text-gray-500 mt-1">Real-time security operations overview — auto-refreshes every 30s</p>
        </div>
        <div className="flex gap-2 items-center">
          <button onClick={requestNotificationPermission} title="Enable browser notifications"
            className={`p-2 rounded-md border ${notificationsEnabled ? 'bg-indigo-50 border-indigo-200 text-indigo-600' : 'bg-white border-gray-300 text-gray-400 hover:text-gray-600'}`}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </button>
          <button onClick={() => setShowCreateAlert(true)} className="px-3 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
            Create Alert
          </button>
          <button onClick={() => setShowCreateIncident(true)} className="px-3 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700">
            Create Incident
          </button>
          <button onClick={() => navigate('/secops/correlations')} className="px-3 py-2 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700">
            Correlations
          </button>
          <button onClick={() => navigate('/secops/threat-hunting')} className="px-3 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50">
            New Threat Hunt
          </button>
        </div>
      </div>

      {/* Executive Summary Widget */}
      <ExecutiveSummary dashboard={dashboard} incidents={incidents} />

      {/* Summary stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Critical Alerts" value={criticalAlerts} color="bg-red-500" onClick={() => navigate('/secops/alerts?severity=critical')} />
        <StatCard label="Open Incidents" value={openIncidents} color="bg-orange-500" onClick={() => navigate('/secops/incidents')} />
        <StatCard label="Active Hunts" value={activeHunts} color="bg-indigo-500" onClick={() => navigate('/secops/threat-hunting')} />
        <StatCard label="Dark Web Exposures" value={exposures} color="bg-purple-500" onClick={() => navigate('/secops/dark-web')} />
      </div>

      {/* Cross-Product Health Strip */}
      <CrossProductHealthStrip />

      {/* Alert Feed + Incident Timeline */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <UnifiedAlertFeed
          alerts={alerts}
          navigate={navigate}
          onAcknowledge={handleAcknowledge}
          onEscalate={handleEscalate}
          onDismiss={handleDismiss}
        />
        <IncidentTimeline incidents={incidents} navigate={navigate} />
      </div>

      {/* MITRE ATT&CK Heat Map */}
      <MitreHeatMap mitreHits={mitreHits} alerts={alerts} navigate={navigate} />

      {/* Alert Severity Breakdown */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-4 text-sm flex-wrap">
          <span className="text-gray-500 font-medium">Alert Breakdown:</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-red-500" /> Critical: {criticalAlerts}</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-orange-400" /> High: {alerts.filter(a => a.severity === 'high').length}</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-yellow-400" /> Medium: {alerts.filter(a => a.severity === 'medium').length}</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-blue-400" /> Low: {alerts.filter(a => a.severity === 'low').length}</span>
          {dashboard?.alert_sources && Object.keys(dashboard.alert_sources).length > 0 && (
            <>
              <span className="text-gray-300 mx-1">|</span>
              <span className="text-gray-500 font-medium">By Source:</span>
              {Object.entries(dashboard.alert_sources).map(([src, count]) => (
                <span key={src} className="flex items-center gap-1"><SourceIcon source={src} /> {src}: {count}</span>
              ))}
            </>
          )}
        </div>
      </div>

      {/* Create Alert Modal */}
      {showCreateAlert && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreateAlert(false)}>
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create Alert</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input type="text" value={newAlert.title} onChange={e => setNewAlert(a => ({ ...a, title: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" placeholder="Alert title..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
                <select value={newAlert.severity} onChange={e => setNewAlert(a => ({ ...a, severity: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500">
                  {['critical', 'high', 'medium', 'low', 'info'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <input type="text" value={newAlert.category} onChange={e => setNewAlert(a => ({ ...a, category: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" placeholder="e.g., malware, intrusion, phishing" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                <input type="text" value={newAlert.source} onChange={e => setNewAlert(a => ({ ...a, source: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" placeholder="e.g., EDR, SIEM, manual" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowCreateAlert(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              <button onClick={handleCreateAlert} disabled={creating || !newAlert.title}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50">
                {creating ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Incident Modal */}
      {showCreateIncident && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreateIncident(false)}>
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create Incident</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input type="text" value={newIncident.title} onChange={e => setNewIncident(a => ({ ...a, title: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" placeholder="Incident title..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
                <select value={newIncident.severity} onChange={e => setNewIncident(a => ({ ...a, severity: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500">
                  {['critical', 'high', 'medium', 'low'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={newIncident.description} onChange={e => setNewIncident(a => ({ ...a, description: e.target.value }))}
                  rows={3} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500" placeholder="Describe the incident..." />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowCreateIncident(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              <button onClick={handleCreateIncident} disabled={creating || !newIncident.title}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50">
                {creating ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
